/**
 * ManageAnnouncements.js
 *
 * FIXES:
 * 1. Wrong endpoint: /announcements/all → /announcements
 *    (404: Route GET /api/v1/announcements/all not found)
 * 2. Wrong toggle endpoint: /announcements/toggle/:id → /announcements/:id/toggle
 *    (standard RESTful pattern used by the backend)
 * 3. Missing delete handler wired to Trash2 button
 * 4. Empty state when no announcements exist
 * 5. Loading state added
 * 6. Role check: employees see read-only view (no Compose button)
 *
 * All original UI/layout preserved exactly.
 */
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Badge, Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Plus, Trash2, Power, Send, Bell } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const ADMIN_ROLES = ['admin', 'hr-manager', 'account-manager', 'super-admin'];

const ManageAnnouncements = () => {
    const { user } = useAuth();
    const isAdmin  = ADMIN_ROLES.includes(user?.role);

    const [notices,   setNotices]   = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [saving,    setSaving]    = useState(false);
    const [formData,  setFormData]  = useState({
        title: '', message: '', priority: 'Medium',
        target_audience: 'All', expiry_date: '',
    });

    useEffect(() => { fetchAllNotices(); }, []);

    const fetchAllNotices = async () => {
        setLoading(true);
        try {
            // FIX 1: Correct endpoint — was /announcements/all (404)
            const res = await axiosInstance.get('/announcements');
            setNotices(res.data.data || []);
        } catch (err) {
            toast.error('Failed to load announcements');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!formData.title.trim() || !formData.message.trim()) {
            toast.error('Title and message are required');
            return;
        }
        setSaving(true);
        try {
            await axiosInstance.post('/announcements', formData);
            toast.success('Announcement published!');
            setShowModal(false);
            setFormData({ title: '', message: '', priority: 'Medium', target_audience: 'All', expiry_date: '' });
            fetchAllNotices();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Publication failed');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (id) => {
        try {
            // FIX 2: Correct endpoint — was /announcements/toggle/:id
            await axiosInstance.patch(`/announcements/${id}/toggle`);
            toast.success('Status updated');
            fetchAllNotices();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update status');
        }
    };

    // FIX 3: Delete was wired to button but handler didn't exist
    const handleDelete = async (id, title) => {
        if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
        try {
            await axiosInstance.delete(`/announcements/${id}`);
            toast.success('Announcement deleted');
            fetchAllNotices();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete');
        }
    };

    return (
        <div className="manage-announcements p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <Bell size={22} className="text-primary"/>
                    {isAdmin ? 'Internal Communications' : 'Company Announcements'}
                </h4>
                {/* FIX 6: Only admins see Compose button */}
                {isAdmin && (
                    <Button variant="primary"
                        onClick={() => setShowModal(true)}
                        className="d-flex align-items-center gap-2">
                        <Plus size={18}/> Compose Notice
                    </Button>
                )}
            </div>

            <Card className="border-0 shadow-sm">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary"/>
                        <p className="text-muted small mt-2">Loading announcements…</p>
                    </div>
                ) : notices.length === 0 ? (
                    /* FIX 4: Empty state */
                    <div className="text-center py-5 text-muted">
                        <Bell size={36} className="mb-2 opacity-30"/>
                        <p className="fw-bold mb-1">No announcements yet</p>
                        <p className="small mb-0">
                            {isAdmin
                                ? 'Click "Compose Notice" to publish your first company update.'
                                : 'Check back later for company updates and notices.'}
                        </p>
                    </div>
                ) : (
                    <Table hover responsive className="align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Notice Title</th>
                                <th>Message</th>
                                <th>Audience</th>
                                <th>Priority</th>
                                <th>Expiry</th>
                                <th>Status</th>
                                {isAdmin && <th className="text-end">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {notices.map(n => (
                                <tr key={n.id}>
                                    <td className="fw-bold" style={{ maxWidth: 180 }}>{n.title}</td>
                                    <td style={{ maxWidth: 250 }}>
                                        <small className="text-muted">
                                            {(n.message || '').slice(0, 80)}
                                            {(n.message || '').length > 80 ? '…' : ''}
                                        </small>
                                    </td>
                                    <td><small>{n.target_audience || 'All'}</small></td>
                                    <td>
                                        <Badge bg={
                                            n.priority === 'Critical' ? 'danger' :
                                            n.priority === 'High'     ? 'warning' :
                                            n.priority === 'critical' ? 'danger' :
                                            n.priority === 'important'? 'warning' :
                                            'info'
                                        }>
                                            {n.priority}
                                        </Badge>
                                    </td>
                                    <td>
                                        <small>{n.expiry_date
                                            ? new Date(n.expiry_date).toLocaleDateString('en-IN')
                                            : 'No Expiry'}
                                        </small>
                                    </td>
                                    <td>
                                        <Badge bg={n.is_active ? 'success' : 'secondary'}>
                                            {n.is_active ? 'Active' : 'Archived'}
                                        </Badge>
                                    </td>
                                    {isAdmin && (
                                        <td className="text-end">
                                            <Button variant="link" className="text-muted p-0 me-3"
                                                title={n.is_active ? 'Deactivate' : 'Activate'}
                                                onClick={() => handleToggle(n.id)}>
                                                <Power size={18}/>
                                            </Button>
                                            {/* FIX 3: Delete now calls handleDelete */}
                                            <Button variant="link" className="text-danger p-0"
                                                title="Delete"
                                                onClick={() => handleDelete(n.id, n.title)}>
                                                <Trash2 size={18}/>
                                            </Button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card>

            {/* Compose Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">Create Announcement</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCreate}>
                    <Modal.Body className="bg-light p-4 rounded mx-3 mb-3">
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Title *</Form.Label>
                            <Form.Control
                                required
                                placeholder="e.g. Office Holiday Notice"
                                value={formData.title}
                                onChange={e => setFormData({ ...formData, title: e.target.value })}/>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Message Content *</Form.Label>
                            <Form.Control
                                as="textarea" rows={4} required
                                placeholder="Write your announcement here..."
                                value={formData.message}
                                onChange={e => setFormData({ ...formData, message: e.target.value })}/>
                        </Form.Group>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold">Priority</Form.Label>
                                    <Form.Select
                                        value={formData.priority}
                                        onChange={e => setFormData({ ...formData, priority: e.target.value })}>
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Critical">Critical</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold">Target Audience</Form.Label>
                                    <Form.Select
                                        value={formData.target_audience}
                                        onChange={e => setFormData({ ...formData, target_audience: e.target.value })}>
                                        <option value="All">All Employees</option>
                                        <option value="Management">Management Only</option>
                                        <option value="Staff">General Staff</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold">Expiry Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        min={new Date().toISOString().slice(0,10)}
                                        value={formData.expiry_date}
                                        onChange={e => setFormData({ ...formData, expiry_date: e.target.value })}/>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button variant="primary" type="submit"
                            className="d-flex align-items-center gap-2" disabled={saving}>
                            {saving
                                ? <><Spinner size="sm"/> Publishing…</>
                                : <><Send size={18}/> Post Announcement</>}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default ManageAnnouncements;
